/**
 * The postMessage contract between the host page (this SDK) and the EHR embed.
 *
 * Rules that make this safe:
 *  - Every message is `{ ns: 'ehrw', v: <protocol>, sessionId, type, ... }`. Anything
 *    without that envelope is ignored, so we never fight with other embeds on the page.
 *  - The host verifies `event.origin === embedOrigin` AND `event.source === iframe.contentWindow`.
 *    Origin alone is not enough: any frame from that origin could otherwise impersonate the embed.
 *  - The host always targets `postMessage(msg, embedOrigin)` — never `'*'`.
 *  - `sessionId` is per-open, so a stale frame being torn down cannot drive the new one.
 *
 * @author Rajkumar Rathod
 */
import { MSG_NS, PROTOCOL_VERSION } from './constants';
import type {
  CloseReason,
  PrefillData,
  Theme,
  WidgetEventName,
  WidgetEventMap,
  WidgetFlow,
} from './types';

export interface Envelope {
  ns: typeof MSG_NS;
  v: number;
  sessionId: string;
}

/* ---------- host -> embed ---------- */

export interface HostInitMessage extends Envelope {
  type: 'host:init';
  clientId: string;
  /** Tenant the embed should scope its API calls to. Defaults to `clientId`. */
  tenantId: string;
  /** Clinic name to show in the embed. Empty when the host tag omits it. */
  tenantName: string;
  flow: WidgetFlow;
  sdkVersion: string;
  /** The host page's origin, so the embed can check it against the tenant allowlist. */
  parentOrigin: string;
  /** Base URL for the embed's REST calls. Sent here so it is configurable per client. */
  apiBaseUrl: string;
  theme: Theme;
  locale: string;
  prefill: PrefillData;
  token: string | null;
  /** Tells the embed whether to render its own close affordance. */
  hostRendersCloseButton: boolean;
}

export interface HostTokenMessage extends Envelope {
  type: 'host:token';
  token: string | null;
}

/**
 * Notification that the host is tearing the frame down. Not a request — the embed
 * cannot veto it, so use it to flush analytics or abandon a draft, not to prompt.
 * Hosts that need the embed to own dismissal should disable the SDK's own
 * affordances (`showCloseButton`/`closeOnEsc`/`closeOnBackdrop`: false).
 */
export interface HostCloseMessage extends Envelope {
  type: 'host:close';
  /**
   * Why. The embed needs this to tell the patient leaving from the SDK swapping
   * one flow for another (`'api'`), which tears the frame down just as hard but
   * means the visit is continuing in the next one — anything the embed discards
   * on the way out would be discarded mid-visit.
   *
   * Added after the fact, so an embed built against the first cut of v1 simply
   * does not read it. That is why it is not a version bump.
   */
  reason: CloseReason;
}

export type HostMessage = HostInitMessage | HostTokenMessage | HostCloseMessage;

/* ---------- embed -> host ---------- */

export interface EmbedReadyMessage extends Envelope {
  type: 'embed:ready';
}

export interface EmbedResizeMessage extends Envelope {
  type: 'embed:resize';
  /** Content height in CSS pixels. */
  height: number;
}

export interface EmbedCloseMessage extends Envelope {
  type: 'embed:close';
}

export interface EmbedEventMessage extends Envelope {
  type: 'embed:event';
  name: WidgetEventName;
  payload: unknown;
}

export interface EmbedTokenRequestMessage extends Envelope {
  type: 'embed:token-request';
}

export interface EmbedErrorMessage extends Envelope {
  type: 'embed:error';
  code: string;
  message: string;
}

export type EmbedMessage =
  | EmbedReadyMessage
  | EmbedResizeMessage
  | EmbedCloseMessage
  | EmbedEventMessage
  | EmbedTokenRequestMessage
  | EmbedErrorMessage;

/** Events the embed is allowed to forward. Anything else is dropped. */
export const ALLOWED_EMBED_EVENTS: ReadonlySet<string> = new Set<keyof WidgetEventMap>([
  'appointment.booked',
  'appointment.cancelled',
  'appointment.rescheduled',
  'patient.created',
  'patient.identified',
  'navigate',
]);

export function isEmbedMessage(data: unknown): data is EmbedMessage {
  if (typeof data !== 'object' || data === null) return false;
  const m = data as Record<string, unknown>;
  return (
    m['ns'] === MSG_NS &&
    m['v'] === PROTOCOL_VERSION &&
    typeof m['sessionId'] === 'string' &&
    typeof m['type'] === 'string' &&
    (m['type'] as string).startsWith('embed:')
  );
}

export function envelope(sessionId: string): Envelope {
  return { ns: MSG_NS, v: PROTOCOL_VERSION, sessionId };
}
